import React, { useState, useEffect } from "react";
import {
  Layout,
  Input,
  Select,
  Button,
  Card,
  Typography,
  Space,
  Radio,
  message,
  Spin,
  Divider,
  Drawer,
  FloatButton,
} from "antd";
import {
  BookOutlined,
  SendOutlined,
  ReadOutlined,
  EditOutlined,
  AudioOutlined,
  CommentOutlined,
  BulbOutlined,
  HighlightOutlined,
  MenuOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import { GradeLevel, SkillType, ExamType } from "./types";
import { fetchAIResponse } from "./services/aiService";
import { buildPrompt } from "./utils/promptBuilder";
import MarkdownRender from "./components/MarkdownRender";
import "./App.css";

const { Header, Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// --- REUSABLE SIDEBAR CONTENT ---
interface SidebarProps {
  grade: GradeLevel;
  setGrade: (val: GradeLevel) => void;
  exam: ExamType;
  setExam: (val: ExamType) => void;
  skill: SkillType;
  setSkill: (val: SkillType) => void;
}

const SidebarContent: React.FC<SidebarProps> = ({
  grade,
  setGrade,
  exam,
  setExam,
  skill,
  setSkill,
}) => {
  // Logic kiểm tra disable các lớp dựa trên kỳ thi
  const isGradeDisabled = (gradeNum: number) => {
    // Nếu chọn thi Tốt nghiệp Tiểu học: Disable lớp > 5 (Disable 6-12)
    if (exam === "tot-nghiep-th") return gradeNum > 5;

    // Nếu chọn thi Tốt nghiệp THCS (Vào 10): Disable < 6 hoặc > 9 (Disable 1-5, 10-12)
    if (exam === "tot-nghiep-thcs") return gradeNum < 6 || gradeNum > 9;

    // Nếu chọn thi Tốt nghiệp THPT: Disable < 10 (Disable 1-9)
    if (exam === "tot-nghiep-thpt") return gradeNum < 10;

    return false;
  };

  // Tự động chuyển về lớp hợp lệ nếu lớp hiện tại bị disable (UX Improvement)
  useEffect(() => {
    const currentGradeNum = parseInt(grade);
    if (isGradeDisabled(currentGradeNum)) {
      if (exam === "tot-nghiep-th") setGrade("5");
      if (exam === "tot-nghiep-thcs") setGrade("9");
      if (exam === "tot-nghiep-thpt") setGrade("12");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam]);

  return (
    <>
      <div className="logo-container">
        <BookOutlined style={{ fontSize: "48px", color: "#006d75" }} />
        <Title level={3} className="app-title">
          Trợ Lý Văn Học
        </Title>
        <Text
          type="secondary"
          style={{ fontFamily: "Nunito", fontSize: "14px" }}
        >
          Đồng hành cùng học sinh Việt Nam
        </Text>
      </div>

      <div style={{ padding: "24px" }}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          {/* Grade Selection */}
          <div className="control-group">
            <Text strong style={{ fontSize: 16, color: "#006d75" }}>
              <BulbOutlined /> Cấp độ học tập
            </Text>
            <Select
              value={grade}
              onChange={setGrade}
              style={{ width: "100%", marginTop: 8 }}
              size="large"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((i) => (
                <Option
                  key={i}
                  value={String(i)}
                  disabled={isGradeDisabled(i)} // Áp dụng logic disable
                >
                  Lớp {i}
                </Option>
              ))}
            </Select>
          </div>

          <Divider style={{ margin: "12px 0" }} dashed />

          {/* Exam Mode */}
          <div className="control-group">
            <Text strong style={{ fontSize: 16, color: "#006d75" }}>
              <HighlightOutlined /> Mục tiêu ôn tập
            </Text>
            <Select
              value={exam}
              onChange={setExam}
              style={{ width: "100%", marginTop: 8 }}
              size="large"
            >
              <Option value="none">📚 Học tập hàng ngày</Option>
              <Option value="giua-ky">📝 Thi Giữa Kỳ</Option>
              <Option value="cuoi-ky">📝 Thi Cuối Kỳ</Option>
              <Option value="tot-nghiep-th">🎓 Tốt nghiệp Tiểu học</Option>
              <Option value="tot-nghiep-thcs">🎓 Vào lớp 10 (THCS)</Option>
              <Option value="tot-nghiep-thpt">🎓 Tốt nghiệp THPT QG</Option>
            </Select>
          </div>

          <Divider style={{ margin: "12px 0" }} dashed />

          {/* Skill Selection */}
          <div className="control-group">
            <Text strong style={{ fontSize: 16, color: "#006d75" }}>
              ✨ Kỹ năng rèn luyện
            </Text>
            <Radio.Group
              value={skill}
              onChange={(e) => setSkill(e.target.value)}
              buttonStyle="solid"
              style={{ marginTop: 12, width: "100%" }}
              className="custom-radio-group"
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Radio.Button
                  value="doc"
                  style={{ width: "100%", height: "auto", padding: "8px 15px" }}
                >
                  <ReadOutlined style={{ marginRight: 8 }} /> Đọc hiểu văn bản
                </Radio.Button>
                <Radio.Button
                  value="viet"
                  style={{ width: "100%", height: "auto", padding: "8px 15px" }}
                >
                  <EditOutlined style={{ marginRight: 8 }} /> Viết & Làm văn
                </Radio.Button>
                <Radio.Button
                  value="nghe"
                  style={{ width: "100%", height: "auto", padding: "8px 15px" }}
                >
                  <AudioOutlined style={{ marginRight: 8 }} /> Luyện Nghe
                </Radio.Button>
                <Radio.Button
                  value="noi"
                  style={{ width: "100%", height: "auto", padding: "8px 15px" }}
                >
                  <CommentOutlined style={{ marginRight: 8 }} /> Luyện Nói &
                  Hùng biện
                </Radio.Button>
              </Space>
            </Radio.Group>
          </div>
        </Space>
      </div>
    </>
  );
};

// --- MAIN APP COMPONENT ---

const App: React.FC = () => {
  const [grade, setGrade] = useState<GradeLevel>("9");
  const [skill, setSkill] = useState<SkillType>("viet");
  const [exam, setExam] = useState<ExamType>("none");

  const [input, setInput] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  // State for Mobile Drawer visibility
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleGenerate = async () => {
    if (!input.trim()) {
      message.warning(
        "Bạn hãy nhập câu hỏi hoặc đề bài để thầy/cô trợ giúp nhé!",
      );
      return;
    }
    setLoading(true);
    setResult("");
    try {
      const fullPrompt = buildPrompt(grade, skill, exam, input);
      const aiResponse = await fetchAIResponse(fullPrompt);
      setResult(aiResponse);
    } catch (error) {
      message.error("Kết nối gián đoạn. Vui lòng thử lại sau.");
    } finally {
      setLoading(false);
    }
  };

  const getSkillLabel = (s: SkillType) => {
    switch (s) {
      case "doc":
        return "Đọc Hiểu";
      case "viet":
        return "Viết Văn";
      case "nghe":
        return "Nghe";
      case "noi":
        return "Nói";
    }
  };

  // Props for Sidebar to avoid repetition
  const sidebarProps = { grade, setGrade, exam, setExam, skill, setSkill };

  return (
    <Layout className="app-layout">
      {/* 1. DESKTOP SIDER: Visible on large screens, hidden on mobile via CSS */}
      <Sider width={320} theme="light" className="desktop-sider">
        <SidebarContent {...sidebarProps} />
      </Sider>

      {/* 2. MOBILE DRAWER: The Overlay Menu for small screens */}
      <Drawer
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={300}
        styles={{ body: { padding: 0 } }} // Reset padding to match Sider design
        rootClassName="mobile-drawer"
        maskClassName="glass-mask" // Defined in CSS for blur effect
      >
        <SidebarContent {...sidebarProps} />
      </Drawer>

      <Layout style={{ background: "transparent" }}>
        <Header className="custom-header">
          {/* Mobile Hamburger Button */}
          <Button
            className="mobile-menu-btn"
            type="text"
            icon={<MenuOutlined style={{ fontSize: 20 }} />}
            onClick={() => setMobileMenuOpen(true)}
          />

          <div className="header-title">
            <span
              style={{ color: "#8c8c8c", marginRight: 10, fontWeight: 400 }}
              className="hide-on-mobile"
            >
              Góc học tập /
            </span>
            Lớp {grade} <span style={{ margin: "0 8px" }}>•</span>{" "}
            {getSkillLabel(skill)}
          </div>
        </Header>

        <Content className="main-content">
          <Card
            title={
              <span>
                <EditOutlined /> Đề bài / Câu hỏi
              </span>
            }
            bordered={false}
            className="paper-card"
            style={{ marginBottom: 30 }}
          >
            <Paragraph
              type="secondary"
              style={{ fontStyle: "italic", marginBottom: 15 }}
            >
              Nhập đề bài văn, câu hỏi trắc nghiệm hoặc vấn đề bạn cần giải
              đáp...
            </Paragraph>
            <TextArea
              rows={5}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ví dụ: Phân tích tâm trạng nhân vật..."
              className="custom-textarea"
            />
            <div style={{ marginTop: 20, textAlign: "right" }}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                size="large"
                onClick={handleGenerate}
                loading={loading}
                className="btn-primary-custom"
              >
                Bắt đầu học ngay
              </Button>
            </div>
          </Card>

          {(result || loading) && (
            <Card
              title={
                <span>
                  <BookOutlined /> Bài giảng & Gợi ý
                </span>
              }
              bordered={false}
              className="paper-card"
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: "80px 0" }}>
                  <Spin
                    size="large"
                    tip="Đang tra cứu tư liệu và soạn bài..."
                  />
                </div>
              ) : (
                <div className="ai-result-container">
                  <MarkdownRender content={result} />
                </div>
              )}
            </Card>
          )}
        </Content>
      </Layout>
      <FloatButton.BackTop
        type="primary"
        icon={<ArrowUpOutlined />}
        style={{ right: 40, bottom: 40 }}
        visibilityHeight={300}
        tooltip="Lên đầu trang"
      />
    </Layout>
  );
};

export default App;
